import type { ToolParameter } from '@/toolSelection/baseTool.store';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { sqDurationStore } from '@/core/core.stores';
import _ from 'lodash';
import type { AnyProperty } from '@/utilities.types';
import type { Capsule } from '@/utilities/datetime.constants';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  DEFAULT_MONITORING_CONTRAST,
  DEFAULT_NUM_COMPONENTS,
  OutputSignalType,
  OutputType,
} from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.constants';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

export class PrincipalComponentAnalysisStoreV2 extends BaseToolStore {
  static readonly storeName = 'sqPrincipalComponentAnalysisStoreV2';
  type = TREND_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V_2;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    inputSignals: {
      predicate: (parameter: ToolParameter) =>
        parameter?.name !== 'condition' && parameter?.name !== 'restrictToCondition',
      multiple: true,
    },
  };

  initialize() {
    this.state = this.immutable({
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      numComponents: DEFAULT_NUM_COMPONENTS,
      anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
      monitoringContrast: DEFAULT_MONITORING_CONTRAST,
      outputType: OutputType.CONDITION,
      outputSignalType: OutputSignalType.LOGLIKE,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
    });
  }

  get inputSignals() {
    return this.state.get('inputSignals');
  }

  get condition() {
    return this.state.get('condition');
  }

  get restrictToCondition() {
    return this.state.get('restrictToCondition');
  }

  get window() {
    return this.state.get('window');
  }

  get color(): string {
    return this.state.get('color');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numComponents(): number {
    return this.state.get('numComponents');
  }

  get anomalyThreshold(): number {
    return this.state.get('anomalyThreshold');
  }

  get monitoringContrast(): number {
    return this.state.get('monitoringContrast');
  }

  get outputType() {
    return this.state.get('outputType');
  }

  get outputSignalType() {
    return this.state.get('outputSignalType');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration() {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration() {
    return this.state.get('capsuleMergeDuration');
  }

  dehydrate() {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }) {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setColor(payload: { color: string }) {
    this.state.set('color', payload.color);
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }) {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setNumComponents(payload: { numComponents: number }) {
    this.state.set('numComponents', payload.numComponents);
  }

  setAnomalyThreshold(payload: { anomalyThreshold: number }) {
    this.state.set('anomalyThreshold', payload.anomalyThreshold);
  }

  setMonitoringContrast(payload: { monitoringContrast: number }) {
    this.state.set('monitoringContrast', payload.monitoringContrast);
  }

  setOutputType(payload: { outputType: OutputType }) {
    this.state.set('outputType', payload.outputType);
  }

  setOutputSignalType(payload: { outputSignalType: OutputSignalType }) {
    this.state.set('outputSignalType', payload.outputSignalType);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }) {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: ValueWithUnitsItem }) {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: ValueWithUnitsItem }) {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  modifyConfigParams(config: any) {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    PCA_SET_TRAINING_WINDOW: this.setTrainingWindow,
    PCA_SET_COLOR: this.setColor,
    PCA_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    PCA_SET_NUM_COMPONENTS: this.setNumComponents,
    PCA_SET_ANOMALY_THRESHOLD: this.setAnomalyThreshold,
    PCA_SET_MONITORING_CONTRAST: this.setMonitoringContrast,
    PCA_SET_OUTPUT_TYPE: this.setOutputType,
    PCA_SET_OUTPUT_SIGNAL_TYPE: this.setOutputSignalType,
    PCA_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    PCA_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    PCA_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
  };
}
